import axios from 'axios';
import { loginRequest } from './authConfig';

// msalInstance is exported from index.js and imported here to avoid circular deps
let _msalInstance = null;
let _msalReady = Promise.resolve();

export function setMsalInstance(instance, readyPromise) {
  _msalInstance = instance;
  if (readyPromise) _msalReady = readyPromise;
}

// Kept in localStorage so every tab of the site shares the Google sign-in, as
// MSAL does (see authConfig.js).
const _GOOGLE_CRED_KEY = 'qa_google_credential';

export function setGoogleCredential(token) {
  try {
    const parts = token.split('.');
    const pad = (s) => s + '='.repeat((4 - s.length % 4) % 4);
    const payload = JSON.parse(atob(pad(parts[1].replace(/-/g, '+').replace(/_/g, '/'))));
    localStorage.setItem(_GOOGLE_CRED_KEY, JSON.stringify({ token, exp: payload.exp }));
  } catch {}
}

export function clearGoogleCredential() {
  localStorage.removeItem(_GOOGLE_CRED_KEY);
}

// Seconds-since-epoch expiry of the stored Google credential, or null.
export function getGoogleCredentialExp() {
  try {
    const stored = localStorage.getItem(_GOOGLE_CRED_KEY);
    return stored ? JSON.parse(stored).exp : null;
  } catch {
    return null;
  }
}

export function getGoogleCredential() {
  try {
    const stored = localStorage.getItem(_GOOGLE_CRED_KEY);
    if (!stored) return null;
    const { token, exp } = JSON.parse(stored);
    if (exp > Date.now() / 1000) return token;
  } catch {}
  localStorage.removeItem(_GOOGLE_CRED_KEY);
  return null;
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL ?? '',
});

const MOCK_MODE = process.env.REACT_APP_MOCK_MODE === 'true';

// Attach the Azure AD Bearer token to every request automatically
api.interceptors.request.use(async (config) => {
  const impersonateId = localStorage.getItem('impersonateUserId');
  if (impersonateId) {
    config.headers['X-Impersonate-User-ID'] = impersonateId;
  }

  if (MOCK_MODE) {
    config.headers['X-Mock-User'] = localStorage.getItem('mockUserOid') || 'mock-oid';
    return config;
  }
  if (!_msalInstance) return config;

  // Wait for MSAL initialization before calling any MSAL API
  try { await _msalReady; } catch {}

  let account = _msalInstance.getActiveAccount();
  if (!account) {
    const accounts = _msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      account = accounts[0];
      _msalInstance.setActiveAccount(account);
    }
  }
  if (!account) {
    // No MSAL account — check for a valid Google credential
    const googleCred = getGoogleCredential();
    if (googleCred) {
      config.headers.Authorization = `Bearer ${googleCred}`;
      return config;
    }
    // No account yet (MSAL still initializing) — let the request through;
    // the 401 response handler will redirect to login if needed.
    return config;
  }

  try {
    let tokenResponse = await _msalInstance.acquireTokenSilent({
      ...loginRequest,
      account,
    });
    // The ID token is what the API checks, and it lasts an hour, but MSAL
    // decides whether to renew from the access token, which lives 60-90
    // minutes. So it could hand back a cached ID token that had already
    // expired, the API answered 401, and people were sent to sign in again
    // "every hour or so". Renew when the ID token has under 5 minutes left.
    const idExp = tokenResponse.idTokenClaims?.exp;
    if (!idExp || idExp * 1000 - Date.now() < 5 * 60 * 1000) {
      tokenResponse = await _msalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
        forceRefresh: true,
      });
    }
    config.headers.Authorization = `Bearer ${tokenResponse.idToken}`;
  } catch {
    // Silent refresh failed — session fully expired, force re-login
    _msalInstance.acquireTokenRedirect({ ...loginRequest, account });
    return Promise.reject(new Error('Token refresh required'));
  }

  return config;
});

// Redirect to maintenance page when the backend returns 503 maintenance mode
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 503 && error.response?.data?.error === 'maintenance') {
      window.location.href = '/maintenance';
    }
    // Locked mid-session. Not when already there: that page reloads, asks
    // /api/me, gets this same 403, and would reload itself forever.
    if (error.response?.status === 403 && error.response?.data?.error === 'Your account is inactive.'
        && window.location.pathname !== '/account-locked') {
      window.location.href = '/account-locked';
    }
    if (error.response?.status === 401 && !MOCK_MODE) {
      // Every branch below leaves the page, so the reason has to outlive it.
      // Layout shows it once the user is back in the app.
      try { sessionStorage.setItem('harborNotice', 'session'); } catch {}
      // Clear Google credential first (no-op if not a Google session)
      clearGoogleCredential();
      if (!_msalInstance) {
        window.location.replace(window.location.origin);
        return Promise.reject(error);
      }
      // Wait for MSAL initialization before calling any interactive API
      try { await _msalReady; } catch {}
      // Token was rejected — check if there's a real MSAL session to refresh
      const account = _msalInstance.getActiveAccount() || _msalInstance.getAllAccounts()[0];
      if (account) {
        // Known MS account: refresh the token
        try {
          await _msalInstance.acquireTokenRedirect({ ...loginRequest, account });
        } catch {
          await _msalInstance.loginRedirect(loginRequest);
        }
      } else {
        // No MS account and no Google credential — send back to the login page
        window.location.replace(window.location.origin);
      }
    }
    return Promise.reject(error);
  }
);

// The bearer token for a request: a Microsoft ID token, renewed when it has
// under 5 minutes left, else a stored Google credential, else null. Throws if
// a Microsoft account exists but cannot be renewed silently.
async function currentBearerToken() {
  if (!_msalInstance) return getGoogleCredential();
  try { await _msalReady; } catch {}
  let account = _msalInstance.getActiveAccount();
  if (!account) {
    const accounts = _msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      account = accounts[0];
      _msalInstance.setActiveAccount(account);
    }
  }
  if (!account) return getGoogleCredential();

  let tokenResponse = await _msalInstance.acquireTokenSilent({ ...loginRequest, account });
  // The ID token is what the API checks, and it lasts an hour, but MSAL
  // decides whether to renew from the access token, which lives 60-90
  // minutes. So it could hand back a cached ID token that had already
  // expired, the API answered 401, and people were sent to sign in again
  // "every hour or so". Renew when the ID token has under 5 minutes left.
  const idExp = tokenResponse.idTokenClaims?.exp;
  if (!idExp || idExp * 1000 - Date.now() < 5 * 60 * 1000) {
    tokenResponse = await _msalInstance.acquireTokenSilent({ ...loginRequest, account, forceRefresh: true });
  }
  return tokenResponse.idToken;
}

export async function getAuthHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  if (MOCK_MODE) {
    headers['X-Mock-User'] = localStorage.getItem('mockUserOid') || 'mock-oid';
    return headers;
  }
  // Same token as every axios request: the Microsoft ID token (renewed before
  // it expires) or, for a Google sign-in, the stored Google credential. This
  // used to send a possibly expired Microsoft token and never a Google one.
  try {
    const token = await currentBearerToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  } catch {
    // Renewal failed: send without a token and let the backend answer 401.
  }
  return headers;
}

export default api;
